import * as React from "react";
import { useTheme } from "@mui/material/styles";
import { Paper, Typography, Box, Stack } from "@mui/material";

import { PERM_UPPER_RIGHT, PERM_UPPER_LEFT, PERM_LOWER_RIGHT, PERM_LOWER_LEFT, PERMANENT_TO_DECIDUOUS } from "../../../../config/constants/Constants";
import { SECTIONS, getSectionPolygon, getSiteKey as getSiteKeyShared, getToothSize, getOutlinePath, LAYOUT } from "../../../../helper-functions/shared/toothChartConstants";
import { calculatePlaqueStats, calculateBleedingStats, calculateRiskSitesStats } from "../../../../helper-functions/shared/chartUtils";

// ─── Probing depth severity colors ───
const PD_COLORS = {
    pd4: "#ffd64ff1",
    pd4bop: "#ff8965f1",
    pd5: "#ef5350ec",
    pd5bop: "#c62828ef",
    pd6: "#4a148cee"
};

const getPDColor = (pd, bop) => {
    if (!pd || pd < 4) return null;
    if (pd >= 6) return PD_COLORS.pd6;
    if (pd === 5) return bop ? PD_COLORS.pd5bop : PD_COLORS.pd5;
    return bop ? PD_COLORS.pd4bop : PD_COLORS.pd4;
};

export default function PlaqueAndBleedingChart({ teethData, patientInfo = {} }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";
    const chartId = React.useId();

    if (!teethData) return null;

    const upperTeeth = [...PERM_UPPER_RIGHT, ...PERM_UPPER_LEFT];
    const lowerTeeth = [...PERM_LOWER_RIGHT, ...PERM_LOWER_LEFT];

    const getEffectiveTooth = (permanentToothNum) => {
        const deciduousTooth = PERMANENT_TO_DECIDUOUS[permanentToothNum];
        if (deciduousTooth && teethData[deciduousTooth] && !teethData[deciduousTooth].missing && teethData[permanentToothNum]?.missing) {
            return deciduousTooth;
        }
        return permanentToothNum;
    };

    const getSiteKey = getSiteKeyShared;

    // ─── Quadrant-aware site helpers ───
    const isLeftSideTooth = (toothNum) => {
        const q = Math.floor(toothNum / 10);
        return q === 2 || q === 3 || q === 6 || q === 7;
    };

    const getBuccalSites = (toothNum) => (isLeftSideTooth(toothNum) ? ["mb", "b", "db"] : ["db", "b", "mb"]);

    const getInnerSites = (toothNum) => {
        const q = Math.floor(toothNum / 10);
        const isUpper = q <= 2 || (q >= 5 && q <= 6);
        const left = isLeftSideTooth(toothNum);
        if (isUpper) return left ? ["mp", "p", "dp"] : ["dp", "p", "mp"];
        return left ? ["ml", "l", "dl"] : ["dl", "l", "ml"];
    };

    // ─── Shared statistics (same calculation as Summary Statistics panel) ───
    const plaquePercentage = calculatePlaqueStats(teethData).percentage;
    const bleedingPercentage = calculateBleedingStats(teethData).percentage;
    const pdPercentage = calculateRiskSitesStats(teethData).percentage;

    // ─── Layout constants ───
    const { cellWidth, maxRy, rowHeight, svgWidth } = LAYOUT;

    // Label areas for plaque/BOP sections
    const labelArea = 14;
    const svgHeight = labelArea + rowHeight + labelArea + rowHeight + labelArea;
    const topLabelY = labelArea / 2;
    const upperRowY = labelArea + maxRy + 2;
    const midLabelY = labelArea + rowHeight + labelArea / 2;
    const lowerRowY = labelArea + rowHeight + labelArea + maxRy + 2;
    const bottomLabelY = labelArea + rowHeight + labelArea + rowHeight + labelArea / 2;

    // ─── Colors ───
    const bgColor = isDark ? "#3a3a3a" : "#fafafa";
    const lineColor = isDark ? "#777" : "#999";
    const missingLineColor = isDark ? "#666" : "#b0b0b0";
    const labelColor = isDark ? "#888" : "#999";
    const ghostFill = isDark ? "rgba(100,100,100,0.12)" : "rgba(200,200,200,0.25)";
    const toothNumColor = isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)";
    const ghostNumColor = isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.15)";

    const getToothX = (idx) => LAYOUT.getToothX(idx);

    // ─── Render tooth outline with wedge sections ───
    const renderTooth = (permanentToothNum, idx, rowY, dataType, activeColor) => {
        const etn = getEffectiveTooth(permanentToothNum);
        const tooth = teethData[etn];
        const isMissing = !tooth || tooth.missing;
        const { rx, ry } = getToothSize(etn);
        const isImplant = !isMissing && tooth?.implant;
        const r = Math.min(rx, ry);
        const outline = isImplant ? `M ${-r},0 A ${r},${r} 0 1,0 ${r},0 A ${r},${r} 0 1,0 ${-r},0 Z` : getOutlinePath(etn);
        const x = getToothX(idx);
        const clipId = `${chartId}-${dataType}-${permanentToothNum}`;
        const sections = getSectionPolygon(rx, ry);
        const e = 1.5;

        return (
            <g key={permanentToothNum} transform={`translate(${x}, ${rowY})`}>
                <defs>
                    <clipPath id={clipId}>
                        <path d={outline} />
                    </clipPath>
                </defs>

                <path d={outline} fill={isMissing ? ghostFill : bgColor} stroke="none" />

                <g clipPath={`url(#${clipId})`}>
                    {!isMissing && (
                        <>
                            {SECTIONS.map((section) => {
                                const siteKey = getSiteKey(etn, section);
                                if (!tooth[siteKey]?.[dataType]) return null;
                                return <polygon key={section} points={sections[section]} fill={activeColor} />;
                            })}
                            <line x1={-rx * e} y1={-ry * e} x2={rx * e} y2={ry * e} stroke={lineColor} strokeWidth={0.7} />
                            <line x1={rx * e} y1={-ry * e} x2={-rx * e} y2={ry * e} stroke={lineColor} strokeWidth={0.7} />
                            <line x1={-rx * e} y1={0} x2={rx * e} y2={0} stroke={lineColor} strokeWidth={0.7} />
                        </>
                    )}
                </g>

                {/* Tooth number overlay */}
                <text
                    x={0}
                    y={3}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize={9}
                    fontWeight="bold"
                    pointerEvents="none"
                    fill={isMissing ? ghostNumColor : toothNumColor}>
                    {etn}
                </text>

                {isImplant ? (
                    <>
                        <circle cx={0} cy={0} r={r} fill="none" stroke={lineColor} strokeWidth={1.2} />
                        <circle cx={0} cy={0} r={r + 3} fill="none" stroke={lineColor} strokeWidth={1.0} />
                    </>
                ) : (
                    <path
                        d={outline}
                        fill="none"
                        stroke={isMissing ? missingLineColor : lineColor}
                        strokeWidth={isMissing ? 1.0 : 1.2}
                        strokeDasharray={isMissing ? "3,2" : "none"}
                    />
                )}
            </g>
        );
    };

    // ─── Render plaque / BOP section ───
    const renderSection = (title, dataType, activeColor, aimText) => {
        const percentage = dataType === "plaque" ? plaquePercentage : bleedingPercentage;

        return (
            <Paper elevation={0} sx={{ p: { xs: 1, sm: 2 }, width: "100%" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="baseline" mb={1}>
                    <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                        {title}
                    </Typography>

                    <Stack alignItems="flex-end">
                        {patientInfo.name && (
                            <Typography variant="body2" color="text.secondary">
                                {patientInfo.name}
                            </Typography>
                        )}
                        {patientInfo.date && (
                            <Typography variant="body2" color="text.secondary">
                                {patientInfo.date}
                            </Typography>
                        )}
                    </Stack>
                </Stack>

                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Box sx={{ flex: 1, overflow: "auto", "& svg": { maxWidth: "100%", height: "auto" } }}>
                        <svg
                            viewBox={`0 0 ${svgWidth} ${svgHeight}`}
                            width="100%"
                            preserveAspectRatio="xMidYMid meet"
                            style={{ fontFamily: "inherit", display: "block", minWidth: 400 }}>
                            {/* Orientation labels */}
                            <text x={svgWidth / 2} y={topLabelY} textAnchor="middle" dominantBaseline="central" fontSize={10} fill={labelColor} fontStyle="italic">
                                Buccal
                            </text>
                            <text x={svgWidth / 2} y={midLabelY} textAnchor="middle" dominantBaseline="central" fontSize={10} fill={labelColor} fontStyle="italic">
                                Lingual
                            </text>
                            <text x={svgWidth / 2} y={bottomLabelY} textAnchor="middle" dominantBaseline="central" fontSize={10} fill={labelColor} fontStyle="italic">
                                Buccal
                            </text>

                            {upperTeeth.map((tooth, idx) => renderTooth(tooth, idx, upperRowY, dataType, activeColor))}
                            {lowerTeeth.map((tooth, idx) => renderTooth(tooth, idx, lowerRowY, dataType, activeColor))}
                        </svg>
                    </Box>

                    <Stack sx={{ ml: { xs: 1, sm: 2 }, minWidth: { xs: 40, sm: 55 }, textAlign: "right" }}>
                        <Typography variant="h5" fontWeight="bold" sx={{ color: activeColor, whiteSpace: "nowrap" }}>
                            {percentage}%
                        </Typography>
                        <Typography variant="caption" sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
                            {aimText}
                        </Typography>
                    </Stack>
                </Box>
            </Paper>
        );
    };

    // ─── Probing Depth section ───
    const renderPDSection = () => {
        // PD section layout Y positions
        const legendH = 16;
        const orientLabelH = 12;
        const numRowH = 14;
        const midLabelH = 14;

        let cy = 0;
        const pdLegendY = 8;
        cy = legendH;

        const pdTopOrientY = cy + orientLabelH / 2;
        cy += orientLabelH;

        const pdUpperBuccalNumY = cy + numRowH / 2;
        cy += numRowH;

        const pdUpperToothRowY = cy + maxRy + 2;
        cy += rowHeight;

        const pdUpperInnerNumY = cy + numRowH / 2;
        cy += numRowH;

        const pdMidLabelY = cy + midLabelH / 2;
        cy += midLabelH;

        const pdLowerInnerNumY = cy + numRowH / 2;
        cy += numRowH;

        const pdLowerToothRowY = cy + maxRy + 2;
        cy += rowHeight;

        const pdLowerBuccalNumY = cy + numRowH / 2;
        cy += numRowH;

        const pdBottomOrientY = cy + orientLabelH / 2;
        cy += orientLabelH;

        const pdSvgHeight = cy;

        // Render a single PD tooth with severity-colored wedge sections
        const renderPDTooth = (permanentToothNum, idx, rowY) => {
            const etn = getEffectiveTooth(permanentToothNum);
            const tooth = teethData[etn];
            const isMissing = !tooth || tooth.missing;
            const { rx, ry } = getToothSize(etn);
            const isImplant = !isMissing && tooth?.implant;
            const r = Math.min(rx, ry);
            const outline = isImplant ? `M ${-r},0 A ${r},${r} 0 1,0 ${r},0 A ${r},${r} 0 1,0 ${-r},0 Z` : getOutlinePath(etn);
            const x = getToothX(idx);
            const clipId = `${chartId}-pd-${permanentToothNum}`;
            const sections = getSectionPolygon(rx, ry);
            const e = 1.5;

            return (
                <g key={permanentToothNum} transform={`translate(${x}, ${rowY})`}>
                    <defs>
                        <clipPath id={clipId}>
                            <path d={outline} />
                        </clipPath>
                    </defs>

                    <path d={outline} fill={isMissing ? ghostFill : bgColor} stroke="none" />

                    <g clipPath={`url(#${clipId})`}>
                        {!isMissing && (
                            <>
                                {SECTIONS.map((section) => {
                                    const siteKey = getSiteKey(etn, section);
                                    const pd = tooth[siteKey]?.probing_depth || 0;
                                    const bop = tooth[siteKey]?.bleeding || false;
                                    const color = getPDColor(pd, bop);
                                    if (!color) return null;
                                    return <polygon key={section} points={sections[section]} fill={color} fillOpacity={0.75} />;
                                })}
                                <line x1={-rx * e} y1={-ry * e} x2={rx * e} y2={ry * e} stroke={lineColor} strokeWidth={0.7} />
                                <line x1={rx * e} y1={-ry * e} x2={-rx * e} y2={ry * e} stroke={lineColor} strokeWidth={0.7} />
                                <line x1={-rx * e} y1={0} x2={rx * e} y2={0} stroke={lineColor} strokeWidth={0.7} />
                            </>
                        )}
                    </g>

                    <text
                        x={0}
                        y={3}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontSize={9}
                        fontWeight="bold"
                        pointerEvents="none"
                        fill={isMissing ? ghostNumColor : toothNumColor}>
                        {etn}
                    </text>

                    {isImplant ? (
                        <>
                            <circle cx={0} cy={0} r={r} fill="none" stroke={lineColor} strokeWidth={1.2} />
                            <circle cx={0} cy={0} r={r + 3} fill="none" stroke={lineColor} strokeWidth={1.0} />
                        </>
                    ) : (
                        <path
                            d={outline}
                            fill="none"
                            stroke={isMissing ? missingLineColor : lineColor}
                            strokeWidth={isMissing ? 1.0 : 1.2}
                            strokeDasharray={isMissing ? "3,2" : "none"}
                        />
                    )}
                </g>
            );
        };

        // Render 3 probing depth numbers per tooth
        const renderPDNumbers = (teethArr, y, getSitesFn) => {
            return teethArr.map((ptn, idx) => {
                const etn = getEffectiveTooth(ptn);
                const tooth = teethData[etn];
                if (!tooth || tooth.missing) return null;

                const x = getToothX(idx);
                const sites = getSitesFn(etn);
                const numSpacing = cellWidth / 3;

                return (
                    <g key={ptn}>
                        {sites.map((site, sIdx) => {
                            const pd = tooth[site]?.probing_depth || 0;
                            if (pd === 0) return null;
                            const bop = tooth[site]?.bleeding || false;
                            const isElevated = pd >= 4;
                            const color = isElevated ? getPDColor(pd, bop) : isDark ? "#aaa" : "#888";
                            const siteX = x + (sIdx - 1) * numSpacing;

                            return (
                                <text
                                    key={site}
                                    x={siteX}
                                    y={y}
                                    textAnchor="middle"
                                    dominantBaseline="central"
                                    fontSize={isElevated ? 10 : 8}
                                    fontWeight={isElevated ? "bold" : "normal"}
                                    fill={color}>
                                    {pd}
                                </text>
                            );
                        })}
                    </g>
                );
            });
        };

        // Color legend
        const legendItems = [
            { label: "4mm no BOP", color: PD_COLORS.pd4 },
            { label: "4mm + BOP", color: PD_COLORS.pd4bop },
            { label: "5mm no BOP", color: PD_COLORS.pd5 },
            { label: "5mm + BOP", color: PD_COLORS.pd5bop },
            { label: "6mm or more", color: PD_COLORS.pd6 }
        ];
        const legendItemW = svgWidth / legendItems.length;

        return (
            <Paper elevation={0} sx={{ p: { xs: 1, sm: 2 }, width: "100%" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="baseline" mb={1}>
                    <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                        Probing Depths ≥ 4mm
                    </Typography>
                    <Stack alignItems="flex-end">
                        {patientInfo.name && (
                            <Typography variant="body2" color="text.secondary">
                                {patientInfo.name}
                            </Typography>
                        )}
                        {patientInfo.date && (
                            <Typography variant="body2" color="text.secondary">
                                {patientInfo.date}
                            </Typography>
                        )}
                    </Stack>
                </Stack>

                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Box sx={{ flex: 1, overflow: "auto", "& svg": { maxWidth: "100%", height: "auto" } }}>
                        <svg
                            viewBox={`0 0 ${svgWidth} ${pdSvgHeight}`}
                            width="100%"
                            preserveAspectRatio="xMidYMid meet"
                            style={{ fontFamily: "inherit", display: "block", minWidth: 400 }}>
                            {/* Color legend */}
                            {legendItems.map((item, i) => (
                                <g key={item.label} transform={`translate(${legendItemW * i + legendItemW / 2 - 32}, ${pdLegendY})`}>
                                    <rect x={0} y={-5} width={10} height={10} rx={1} fill={item.color} />
                                    <text x={14} y={0} fontSize={8} fill={isDark ? "#ccc" : "#555"} dominantBaseline="central">
                                        {item.label}
                                    </text>
                                </g>
                            ))}

                            {/* Top orientation: Buccal */}
                            <text x={svgWidth / 2} y={pdTopOrientY} textAnchor="middle" fontSize={9} fill={labelColor} fontStyle="italic" dominantBaseline="central">
                                Buccal
                            </text>

                            {/* Upper arch: buccal numbers → teeth → palatal numbers */}
                            {renderPDNumbers(upperTeeth, pdUpperBuccalNumY, getBuccalSites)}
                            {upperTeeth.map((tooth, idx) => renderPDTooth(tooth, idx, pdUpperToothRowY))}
                            {renderPDNumbers(upperTeeth, pdUpperInnerNumY, getInnerSites)}

                            {/* Middle label */}
                            <text x={svgWidth / 2} y={pdMidLabelY} textAnchor="middle" dominantBaseline="central" fontSize={10} fill={labelColor} fontStyle="italic">
                                Lingual
                            </text>

                            {/* Lower arch: lingual numbers → teeth → buccal numbers */}
                            {renderPDNumbers(lowerTeeth, pdLowerInnerNumY, getInnerSites)}
                            {lowerTeeth.map((tooth, idx) => renderPDTooth(tooth, idx, pdLowerToothRowY))}
                            {renderPDNumbers(lowerTeeth, pdLowerBuccalNumY, getBuccalSites)}

                            {/* Bottom orientation:Buccal */}
                            <text x={svgWidth / 2} y={pdBottomOrientY} textAnchor="middle" fontSize={9} fill={labelColor} fontStyle="italic" dominantBaseline="central">
                                Buccal
                            </text>
                        </svg>
                    </Box>

                    <Stack sx={{ ml: { xs: 1, sm: 2 }, minWidth: { xs: 40, sm: 55 }, textAlign: "right" }}>
                        <Typography variant="h5" fontWeight="bold" sx={{ color: "#C62828", whiteSpace: "nowrap" }}>
                            {pdPercentage}%
                        </Typography>
                        <Typography variant="caption" sx={{ color: "text.secondary", whiteSpace: "nowrap", fontSize: "0.65rem" }}>
                            {`Aim: all sites`}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "text.secondary", whiteSpace: "nowrap", fontSize: "0.65rem" }}>
                            {`≤ 4mm with`}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "text.secondary", whiteSpace: "nowrap", fontSize: "0.65rem" }}>
                            no BOP
                        </Typography>
                    </Stack>
                </Box>
            </Paper>
        );
    };

    return (
        <Stack spacing={2}>
            {renderSection("Oral Hygiene (Plaque)", "plaque", "#1976d2ea", "Aim: 0-5%")}
            {renderSection("Bleeding on Probing (BOP)", "bleeding", "#d32f2fee", "Aim: < 10%")}
            {renderPDSection()}
        </Stack>
    );
}
